import { useMemo, useState, useCallback } from "react";

import { Tooltip, Typography } from "antd";

import { RVFormItem, RVSelect } from "@regal-voice/ui";
import { Row } from "Components/shared/Flexbox";
import { createSchemaOption } from "Components/shared/RVEventSchemaOption/RVEventSchemaOption";
import { FORM_CONSTS } from "Pages/agentDesktop/Pages/AgentDesktopTasks/Components/AgentTaskInterface/AgentPanes/AgentActionPaneSelection/ContactsPanel/Components/SavedViewContactsPanel/contactsSavedViewsConsts";
import { EventSchemaOptionEntry } from "ServerData/EventSchema/eventSchemaTypes";

import styles from "./ContactListSortOptions.module.scss";

/**
 * List of allowed field types for sorting
 * Only string, number, and datetime fields can be used for sorting
 */
export const ALLOWED_FIELD_TYPES = ["string", "number", "datetime"];

const SORT_OPTIONS = [
    { label: "Ascending", value: "asc" },
    { label: "Descending", value: "desc" },
];

/**
 * @param name The attribute name to categorize
 * @returns An order number for sorting (1: plain string, 2: rvProperties, 3: customProperties)
 */
export function getCategoryOrder(name: string): number {
    if (name.startsWith("rvProperties.")) {
        return 2;
    }
    if (name.startsWith("customProperties.")) {
        return 3;
    }
    return 1; // plain strings first
}

/**
 * Represents a processed attribute ready to be converted into a schema option
 */
export interface ProcessedAttribute {
    displayName: string;
    value: string;
    type: string;
}

/**
 * Processes raw contact attributes into formatted data objects.
 * Options are sorted with plain strings first, followed by rvProperties, then customProperties.
 * Within each category, items are sorted alphabetically.
 * Duplicate attribute names are filtered out to prevent React key issues.
 * Only includes attributes with field types of string, datetime, or number.
 *
 * @param contactAttributes - Raw contact attributes array from the API
 * @returns {Array<ProcessedAttribute>} Processed attribute data objects
 */
export function processAttributeOptions(contactAttributes: Array<EventSchemaOptionEntry>): ProcessedAttribute[] {
    if (!Array.isArray(contactAttributes)) {
        return [];
    }

    // Track attribute names that have already been processed to avoid duplicates
    const processedAttributeNames = new Set<string>();

    // Create options from raw contact attributes, filtering out duplicates
    const uniqueOptions = contactAttributes
        .map((item): ProcessedAttribute | null => {
            if (Array.isArray(item) && item.length > 1 && typeof item[0] === "string") {
                const attributeName = item[0];

                // Skip if this attribute name has already been processed
                if (processedAttributeNames.has(attributeName)) {
                    return null;
                }

                // Extract field type information from the attribute metadata
                let fieldType = "unknown";
                if (item[1] && typeof item[1] === "object" && item[1].fieldType) {
                    fieldType = String(item[1].fieldType).toLowerCase();
                }

                // Skip if field type is not one of the allowed types
                if (!ALLOWED_FIELD_TYPES.includes(fieldType)) {
                    return null;
                }

                // Mark this attribute name as processed
                processedAttributeNames.add(attributeName);

                return {
                    displayName: attributeName,
                    value: attributeName,
                    type: fieldType,
                };
            }
            return null;
        })
        .filter((option): option is ProcessedAttribute => option !== null);

    // Sort the unique options by category and then alphabetically within each category
    return uniqueOptions.sort((a, b) => {
        const aCategory = getCategoryOrder(a.value);
        const bCategory = getCategoryOrder(b.value);

        // First sort by category
        if (aCategory !== bCategory) {
            return aCategory - bCategory;
        }

        // Then sort alphabetically within category
        return a.value.localeCompare(b.value);
    });
}

/**
 * Component for sorting contact lists by available contact attributes.
 * Provides sort field and order selection options in a responsive layout.
 */
type ContactListSortOptionsProps = {
    sortVal?: string;
    contactAttributes: EventSchemaOptionEntry;
};

export const ContactListSortOptions = ({ sortVal, contactAttributes }: ContactListSortOptionsProps) => {
    // Process attribute data and convert it to schema options for the dropdown
    const [showTooltip, setShowTooltip] = useState(true);

    // only show the tooltip when the dropdown is collapsed
    const toolTipText = sortVal && showTooltip ? sortVal : undefined;

    const attributeOptions = useMemo(() => {
        // Ensure contactAttributes is always an array before processing
        const attributesArray = Array.isArray(contactAttributes) ? contactAttributes : [];
        const processedAttributes = processAttributeOptions(attributesArray);

        // Then convert the processed attributes to schema options for the dropdown
        return processedAttributes.map((attr) =>
            createSchemaOption(
                attr.displayName,
                attr.value,
                attr.type,
                undefined, // description
                false // isCustom
            )
        );
    }, [contactAttributes]);

    const handleTooltipVisibleChange = useCallback((visible: boolean) => {
        setShowTooltip(!visible);
    }, []);

    return (
        <Row className={styles.container}>
            <div className={styles.rowContainer} key="sort-by-container">
                <Typography.Text strong className={styles.label}>
                    Sort By
                </Typography.Text>
                <Tooltip title={toolTipText}>
                    <RVFormItem name={FORM_CONSTS.SORT}>
                        <RVSelect
                            onDropdownVisibleChange={handleTooltipVisibleChange}
                            containerClassName={styles.sortSelect}
                            showSearch
                            options={attributeOptions}
                            data-testid="contact-list-sort-field-select"
                        />
                    </RVFormItem>
                </Tooltip>
            </div>

            <div className={styles.rowContainer} key="order-by-container">
                <Typography.Text strong className={styles.label}>
                    Order By
                </Typography.Text>
                <RVFormItem name={FORM_CONSTS.ORDER}>
                    <RVSelect
                        containerClassName={styles.orderSelect}
                        options={SORT_OPTIONS}
                        data-testid="contact-list-sort-order-select"
                    />
                </RVFormItem>
            </div>
        </Row>
    );
};
